// Segment's JavaScript library Analytics.js is imported globally and exposed as
// window.analytics. This module is a minimal wrapper around their library. Its
// main benefit is better tooling for our local environment.

import * as Sentry from '@sentry/react';
import { User } from '../store/types';
import { History, Location } from 'history';
import { store } from '../store';
import { getNameForPath, getTitleForPath } from './url';

const Segment: ObjectMap = {};
const globalProperties: ObjectMap = { module: 'hopthru' };

// Note: `window.isSegmentLoading` is never set to false if set at all.
// Run a local server and set `SEGMENT_WRITE_KEY` if you need to send actual
// events for debugging purposes.
const isSegmentSnippetRendered = window.isSegmentLoading;
let previousLocation: Location;

let isInitialized = false;

const isValidEnvironment = (fnName: string) => {
  if (!isInitialized) {
    Sentry.captureMessage(
      `[Segment] window.analytics.${fnName} called before initialization`,
      'warning'
    );
    return false;
  }

  if (
    window.analytics &&
    typeof (window.analytics as any)[fnName] === 'function'
  ) {
    return true;
  }

  Sentry.captureMessage(
    `[Segment] unable to call window.analytics.${fnName}`,
    'error'
  );
  return false;
};

Segment.initialize = (history: History) => {
  if (isSegmentSnippetRendered && !window.analytics) {
    setTimeout(Segment.initialize, 500);
    return;
  }

  // set up router hook to log to Segment changes to the route
  history.listen((location) => {
    Segment.page(location, previousLocation);
    previousLocation = location;
  });

  isInitialized = true;

  Segment.page(history.location);
};

Segment.identifyUser = (user: User) => {
  if (!user) {
    Sentry.captureMessage(
      '[Segment] Segment.identifyUser called with non-object-like user',
      'warning'
    );
    return;
  }
  if (isValidEnvironment('identify')) {
    const userId = user.email; // users are keyed by email
    const traits = {
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
    }; // includes agencies, modules, etc.
    window.analytics.identify(userId, traits);
  }
};

Segment.associateGroup = (groupId: string, properties = {}) => {
  if (typeof groupId !== 'string' || !groupId) {
    Sentry.captureMessage(
      `[Segment] Segment.associateGroup called with invalid groupId: ${groupId}`,
      'warning'
    );
    return;
  }
  if (isValidEnvironment('group')) {
    window.analytics.group(groupId, properties);
  }
};

Segment.track = (
  event: string,
  args?: {
    properties?: ObjectMap;
    options?: SegmentAnalytics.SegmentOpts;
    callback?: () => void;
  }
) => {
  const agencyKey = store.getState().user.agency?.agencyKey;
  const properties = {
    ...(args?.properties ?? {}),
    agencyKey: agencyKey ?? undefined,
    ...globalProperties,
    // releaseId: process.env.SENTRY_RELEASE_ID ?? 'development',
  };
  if (isValidEnvironment('track')) {
    window.analytics.track(event, properties, args?.options, args?.callback);
  }
};

Segment.page = (to: Location, from?: Location, args: ObjectMap = {}) => {
  if (to !== from) {
    const params = new URLSearchParams(to.search).toString();
    args.properties = {
      ...args.properties,
      from: getNameForPath(from?.pathname),
      agencyKey: store.getState().user.agency?.agencyKey,
      url: `${window.location.origin}${to.pathname}`,
      title: getTitleForPath({ path: to.pathname }),
      path: to.pathname,
      search: params ? `?${params}` : undefined,
      referrer: from ? `${window.location.origin}${from.pathname}` : undefined,
      ...globalProperties,
    };
    if (isValidEnvironment('page')) {
      window.analytics.page(
        'analyze',
        getNameForPath(to.pathname),
        args.properties,
        args.options,
        args.callback
      );
    }
  }
};

Segment.setGlobalProperties = (properties: ObjectMap) => {
  Object.assign(globalProperties, properties);
};

export default Segment;
